import { useWorkDrafts } from '@/stores/workDrafts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSameActionMutation } from '@/hooks/useSingleFlightMutation';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  getPatients,
  getPatient,
  createPatient,
  updatePatient,
  updatePatientAvatar,
  archivePatient,
  restorePatient,
  getPatientCount,
  exportPatientsCsv,
  PatientQueryParams,
  CreatePatientData,
  UpdatePatientData,
} from '@/services/patients';

const PATIENTS_KEY = 'patients';

// Hook for patient list with pagination/search
export function usePatients(params: PatientQueryParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [PATIENTS_KEY, params],
    queryFn: () => getPatients(params),
    enabled: options.enabled ?? true,
  });
}

// Hook for a single patient
export function usePatient(id: string | undefined) {
  return useQuery({
    queryKey: [PATIENTS_KEY, id],
    queryFn: () => getPatient(id!),
    enabled: !!id,
  });
}

// Hook for creating a patient
export function useCreatePatient() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (data: CreatePatientData) => createPatient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] });
      toast.success('Paciente cadastrado com sucesso!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao cadastrar paciente';
      toast.error(message);
    },
  });
}

// Hook for updating a patient
export function useUpdatePatient() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePatientData }) =>
      updatePatient(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY, id] });
      toast.success('Paciente atualizado com sucesso!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao atualizar paciente';
      toast.error(message);
    },
  });
}

// Hook for updating/removing a patient avatar
export function useUpdatePatientAvatar() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: ({ id, avatarUrl }: { id: string; avatarUrl: string | null }) =>
      updatePatientAvatar(id, avatarUrl),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY, id] });
      toast.success('Avatar atualizado com sucesso!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao atualizar avatar';
      toast.error(message);
    },
  });
}

// Hook for archiving a patient
export function useArchivePatient() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (id: string) => archivePatient(id),
    onMutate: () => ({ epoch: useWorkDrafts.getState().epoch }),
    onSuccess: (_, id, context) => {
      const epoch = context?.epoch;
      if (epoch !== useWorkDrafts.getState().epoch) return;
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] });
      toast.success('Paciente arquivado.', { duration: 8000, action: {
        label: 'Desfazer', onClick: () => {
          if (epoch !== useWorkDrafts.getState().epoch) return;
          void restorePatient(id).then(() => {
            if (epoch !== useWorkDrafts.getState().epoch) return;
            void queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] });
            toast.success('Paciente restaurado.');
          }).catch(() => { if (epoch === useWorkDrafts.getState().epoch) toast.error('Não foi possível desfazer. Use Restaurar na ficha.'); });
        },
      } });
    },
    onError: (error: AxiosError<{ message?: string }>, _id, context) => {
      if (context?.epoch !== useWorkDrafts.getState().epoch) return;
      const message = error.response?.data?.message || 'Erro ao arquivar paciente';
      toast.error(message);
    },
  });
}

// Hook for restoring a patient
export function useRestorePatient() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (id: string) => restorePatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] });
      toast.success('Paciente restaurado com sucesso!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao restaurar paciente';
      toast.error(message);
    },
  });
}

// Hook for patient count preview (advanced filters)
export function usePatientCount(params: PatientQueryParams) {
  return useQuery({
    queryKey: [PATIENTS_KEY, 'count', params],
    queryFn: () => getPatientCount(params),
    enabled: Object.keys(params).length > 0,
  });
}

// Hook for exporting patients to CSV
export function useExportPatients() {
  // single-flight-exempt: é leitura (prévia, link de arquivo ou exportação): não grava nada
  return useMutation({
    mutationFn: (params: PatientQueryParams) => exportPatientsCsv(params),
    onSuccess: (blob) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pacientes_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Exportação concluída!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao exportar pacientes';
      toast.error(message);
    },
  });
}
